import { useState, useEffect } from 'react';
import AccordionDescriptions from './AccordionDescriptions';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Modal as BootstrapModal } from 'react-bootstrap';
import { SubmitButton } from '../../elements/buttons';
import { RefreshIcon } from '../../elements/icons';

const searchWebsite = async (searchTerm, searchType) => {

    const normalizeSearchTerm = (searchTerm, searchType) => {
        searchTerm = searchType === 'subdomain' ? `${searchTerm}.fotograf.de` : searchTerm;
        return searchTerm.replace(/^https?:\/\//, '');
    }

    const response = await fetch(`/wp-json/marketing-api/website/search?searchTerm=${normalizeSearchTerm(searchTerm, searchType)}&searchType=${searchType}`);

    if (!response.ok) {
        let errorMessage = null;
        switch (response.status) {
            case 404:
                errorMessage = 'Bitte überprüfen Sie die Webadresse.';
                break;
            default:
                errorMessage = 'Bitte versuchen Sie es erneut.';
                break;
        }
        throw new Error(errorMessage);
    }

    return response.json();
};

const searchWebsiteSchema = yup.object({
    searchTerm: yup.string().required(),
});

const GutentagSearchWebsiteForm = () => {

    const [fotografSubdomain, setFotografSubdomain] = useState(true);
    const [sendRequest, setSendRequest] = useState(false);
    const [prepareRedirectTo, setPrepareRedirectTo] = useState(null);

    const { register, handleSubmit, formState, watch, setError } = useForm({
        resolver: yupResolver(searchWebsiteSchema),
    });

    const formValues = watch();

    const subdomainChangeClickHandler = () => {
        setFotografSubdomain(!fotografSubdomain);
    }

    const getSearchType = () => fotografSubdomain ? 'subdomain' : 'domain';

    const { isFetching, isError, isSuccess, data, error, fetchStatus } = useQuery({
        queryKey: ['website.search'],
        queryFn: () => searchWebsite(
            formValues?.searchTerm,
            getSearchType()
        ),
        enabled: !!sendRequest,
        retry: 0,
    });

    const onSubmitForm = (data) => {
        setSendRequest(true);
    }

    const redirectToWebsite = (redirectTarget) => {
        const hasProtocol = /^(http|https):\/\//i.test(redirectTarget);
        const url = hasProtocol ? redirectTarget : `https://${redirectTarget}`;
        window.location.href = url;
    }

    useEffect(() => {
        if (sendRequest && fetchStatus === 'idle') {
            setSendRequest(false);
        }
    }, [fetchStatus]);

    useEffect(() => {
        if (isError && error?.message) {
            setError('searchTerm', { type: 'custom', message: error?.message });
        }

        if (isSuccess && (data?.domain || data?.subdomain)) {
            setPrepareRedirectTo(getSearchType() === 'domain' ? data?.domain : data?.subdomain);
        }

    }, [isError, isSuccess, data, error]);

    useEffect(() => {

        if (prepareRedirectTo) {
            // Refreshing with a delay to show the green input as the confirmation.
            setTimeout(() => {
                redirectToWebsite(prepareRedirectTo);
            }, 500);
        }

    }, [prepareRedirectTo]);

    return (
        <form onSubmit={handleSubmit(onSubmitForm)}>
            <BootstrapModal.Body>
                <div>
                    Geben Sie hier die Webadresse Ihres Online-Fotografenshops ein.
                </div>
                <div className="my-4">
                    <div
                        className={
                            `input-group input-with-addons ${formState.errors?.searchTerm?.message ? "input-invalid" : ""} ${prepareRedirectTo ? "input-valid" : ""}`
                        }
                    >
                        <div className="input-group-prepend d-none d-sm-block">
                            <span className="input-group-text">https://</span>
                        </div>
                        <input
                            {...register('searchTerm')}
                            type="text"
                            className="form-control rounded-xs-left"
                            placeholder="Seitenname eingeben"
                        />
                        {
                            fotografSubdomain && 
                            <div className="input-group-append">
                                <span className="input-group-text">fotograf.de</span>
                            </div>
                        }
                    </div>
                    {
                        formState.errors?.searchTerm?.message && 
                        <div className="invalid-feedback d-block">
                            Bitte überprüfen Sie die Webadresse.
                        </div>
                    }
                </div>
                <div>
                    <span className="cursor-pointer" onClick={subdomainChangeClickHandler}>
                        {
                            fotografSubdomain ? 'Andere Webadresse' : 'Fotograf.de Webadresse'
                        }
                        <RefreshIcon style={{ marginLeft: '5px' }} />
                    </span>
                </div>
                <div className="mt-4 mb-3">
                    <AccordionDescriptions/>
                </div>
            </BootstrapModal.Body>
            <BootstrapModal.Footer>
                <SubmitButton isLoading={isFetching}>
                    Zum Shop
                </SubmitButton>
            </BootstrapModal.Footer>
        </form>
    );
}

export default GutentagSearchWebsiteForm;
